import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Report when a screen has sat untouched long enough that its contents should be covered.
 *
 * WHY, AND WHY NOW. A clinical screen left facing an exam room is a real exposure, and it is
 * the half of the client's request that was straightforwardly right. It became non-optional
 * once useScreenWakeLock landed: until then a phone put down on a counter blanked itself after
 * a minute and the platform did this work for us. Holding the screen awake to keep recording
 * takes that away, so we owe the cover ourselves.
 *
 * ⚠️ THIS IS A BLUR, NOT A LOCK. Revealing costs one tap and never a credential. An idle
 * timeout that demanded a password would cost more friction than the bad desk microphone this
 * whole feature exists to replace, and re-authentication is separately handled by useIdleTimer
 * at the session level anyway.
 *
 * ⚠️ SEPARATE CLOCK FROM useIdleTimer, ON PURPOSE. That hook owns HIPAA §164.312(a)(2)(iii)
 * automatic logoff, and its shared timestamp has behaviour this must not inherit: it stops
 * refreshing during its own warning countdown, and it is disabled outright for anonymous users.
 * Reading its key would make a phone blur instantly on the flat /chartmind-mic route. Writing
 * to its key would let a blur reveal extend a session that was about to expire. So: same
 * pattern, own key, no coupling in either direction.
 *
 * Cross-tab through localStorage like useIdleTimer, because the exposure is the machine sitting
 * unattended, not one tab of it. Polls wall-clock rather than holding a long timeout, so a
 * laptop that wakes from sleep past the window covers itself immediately instead of resuming a
 * paused timer.
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - Pass false where there is nothing worth covering,
 *   or while the device is actively capturing (see the callers).
 * @param {number} [options.idleMs] - Untouched time before covering.
 * @returns {{ blurred: boolean, reveal: () => void }}
 */

const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "mousedown",
  "touchstart",
  "scroll",
  "click",
];

const LAST_ACTIVITY_KEY = "hd_last_screen_activity";

/** Matches useIdleTimer: once every few seconds keeps the shared clock fresh without thrashing. */
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;
const TICK_MS = 1000;

/** The client's starting point, and a sane one: long enough not to fight a slow conversation. */
export const IDLE_BLUR_MS = 3 * 60 * 1000;

function useIdleBlur({ enabled = true, idleMs = IDLE_BLUR_MS } = {}) {
  const [blurred, setBlurred] = useState(false);
  const blurredRef = useRef(false);
  const lastWriteRef = useRef(0);

  const readLastActivity = useCallback(() => {
    try {
      const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
      const parsed = raw ? parseInt(raw, 10) : NaN;
      // Missing or corrupt reads as "just now", so a bad entry can never blur a screen
      // somebody is looking at.
      return Number.isNaN(parsed) ? Date.now() : parsed;
    } catch {
      return Date.now();
    }
  }, []);

  const writeActivity = useCallback((force = false) => {
    const now = Date.now();
    if (!force && now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) return;
    lastWriteRef.current = now;
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    } catch {
      // Private mode. The in-tab timer below still works; only cross-tab sharing is lost.
    }
  }, []);

  const reveal = useCallback(() => {
    blurredRef.current = false;
    setBlurred(false);
    writeActivity(true);
  }, [writeActivity]);

  useEffect(() => {
    if (!enabled) {
      blurredRef.current = false;
      setBlurred(false);
      return undefined;
    }

    writeActivity(true);

    const handleActivity = () => {
      // ⚠️ Once covered, passive activity must NOT uncover. A mouse nudged by someone walking
      // past the desk, or a phone jostled in a coat pocket, would otherwise reveal the screen
      // in exactly the situation the cover was for. Revealing goes through `reveal`, which the
      // overlay calls on a deliberate tap.
      if (blurredRef.current) return;
      writeActivity(false);
    };

    const tick = () => {
      const idle = Date.now() - readLastActivity() >= idleMs;
      if (idle === blurredRef.current) return;
      blurredRef.current = idle;
      setBlurred(idle);
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true }),
    );
    tick();
    const interval = setInterval(tick, TICK_MS);

    return () => {
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [enabled, idleMs, readLastActivity, writeActivity]);

  return { blurred, reveal };
}

export default useIdleBlur;
